import React from 'react';

import { Navigate } from 'react-router';

import { appRoutes } from '@clearverify/constants';
import type { SimpleObject } from '@clearverify/types';

import { SignUpMethod } from 'src/api';
import { FullscreenLoader } from 'src/components/common';
import { IS_PRODUCTION_BUILD } from 'src/config';
import { SignupConsumerFormStepId } from 'src/constants/auth';
import { useEmployerBasicInfo, useEmployerParamId } from 'src/hooks/employer';
import { useCurrentUserStatesList } from 'src/hooks/states';

import {
  AuthSignupConsumerContext,
  AuthSignupConsumerContextValue,
  FormStepperConsumerState,
} from './AuthSignupConsumerContext';

export const AuthSignupConsumerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const employerId = useEmployerParamId();
  const { employer, loading } = useEmployerBasicInfo(employerId);

  const [formData, setFormData] = React.useState<SimpleObject>({});
  const [employeeSignupToken, setEmployeeSignupToken] = React.useState<string | null>(null);

  const homeAddress = formData.homeAddress as
    | {
        state: string | undefined | null;
      }
    | undefined;

  const { isRequestAutoApproveStatusInactive } = useCurrentUserStatesList({
    state: homeAddress?.state,
  });

  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);

  const consumerFormFlow = React.useMemo(() => {
    return [
      employer?.signUpMethod === SignUpMethod.Ssn
        ? SignupConsumerFormStepId.ssn
        : SignupConsumerFormStepId.pin,
      SignupConsumerFormStepId.consumerBasicInfo,
      SignupConsumerFormStepId.consumerAttestations,
      ...(isRequestAutoApproveStatusInactive
        ? []
        : [SignupConsumerFormStepId.consumerVerificationPreference]),
      SignupConsumerFormStepId.consumerReviewData,
    ];
  }, [employer?.signUpMethod, isRequestAutoApproveStatusInactive]);

  const formStepperState = React.useMemo((): FormStepperConsumerState => {
    const nextStepId = consumerFormFlow[currentStepIndex + 1];

    return {
      previousStepId: consumerFormFlow[currentStepIndex - 1],
      currentStepId: consumerFormFlow[currentStepIndex] as string,
      nextStepId,
      isNextStepAvailable: Boolean(nextStepId),
      isLastStep: currentStepIndex === consumerFormFlow.length - 1,
    };
  }, [currentStepIndex, consumerFormFlow]);

  const goToNextStep = React.useCallback(() => {
    if (formStepperState.nextStepId) {
      setCurrentStepIndex(currentIndex => currentIndex + 1);
    }
  }, [formStepperState.nextStepId]);

  const goToPreviousStep = React.useCallback(() => {
    if (formStepperState.previousStepId) {
      setCurrentStepIndex(currentIndex => currentIndex - 1);
    }
  }, [formStepperState.previousStepId]);

  const updateFormData = React.useCallback((formValues: SimpleObject) => {
    setFormData(currentFormData => {
      return { ...currentFormData, ...formValues };
    });
  }, []);

  const contextValue: AuthSignupConsumerContextValue = React.useMemo(() => {
    return {
      employerInfo: employer,
      loading,
      formData,
      setFormData,
      updateFormData,
      employeeSignupToken,
      setEmployeeSignupToken,
      formStepperState,
      goToNextStep,
      goToPreviousStep,
    };
  }, [
    employer,
    formData,
    formStepperState,
    employeeSignupToken,
    goToNextStep,
    goToPreviousStep,
    loading,
    updateFormData,
  ]);

  if (!employerId || (IS_PRODUCTION_BUILD && employerId && !loading && !employer)) {
    return <Navigate to={appRoutes.auth.index} />;
  }

  if (loading) {
    return <FullscreenLoader />;
  }

  return <AuthSignupConsumerContext value={contextValue}>{children}</AuthSignupConsumerContext>;
};
